import { Building2, MapPin, AlertTriangle, Edit2, Trash2, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Facility {
    id: string;
    name: string;
    address: string;
    totalArea: number;
    sectorType: 'TERTIARY' | 'INDUSTRIAL';
    riskLevel: 'STANDARD' | 'ELEVATED' | 'HIGH';
    createdAt: number;
    updatedAt: number;
}

interface FacilityCardProps {
    facility: Facility;
    onEdit: (facility: Facility) => void;
    onDelete: (facilityId: string) => void;
}

const getRiskLevelBadge = (level: string) => {
    switch (level) {
        case 'HIGH':
            return 'badge-danger';
        case 'ELEVATED':
            return 'badge-warning';
        default:
            return 'badge-info';
    }
};

const getSectorIcon = (sector: string) => {
    return sector === 'INDUSTRIAL' ? '🏭' : '🏢';
};

export default function FacilityCard({ facility, onEdit, onDelete }: FacilityCardProps) {
    return (
        <div className="card-glass p-6 hover:shadow-xl transition-all duration-300 group animate-slideUp">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-steel-500 to-steel-600 rounded-xl flex items-center justify-center text-2xl shadow-md group-hover:scale-110 transition-transform">
                        {getSectorIcon(facility.sectorType)}
                    </div>
                    <div>
                        <h3 className="font-display font-semibold text-lg text-charcoal-950 group-hover:text-ember-600 transition-colors">
                            {facility.name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-steel-600 mt-1">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate max-w-xs">{facility.address}</span>
                        </div>
                    </div>
                </div>

                {/* Risk Badge */}
                <span className={`badge ${getRiskLevelBadge(facility.riskLevel)} flex items-center gap-1`}>
                    {facility.riskLevel === 'HIGH' && <AlertTriangle className="w-3 h-3" />}
                    {facility.riskLevel}
                </span>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t border-steel-200">
                <div>
                    <p className="text-xs text-steel-500 mb-1">Total Area</p>
                    <p className="font-semibold text-charcoal-900">{facility.totalArea.toLocaleString()} m²</p>
                </div>
                <div>
                    <p className="text-xs text-steel-500 mb-1">Sector Type</p>
                    <p className="font-semibold text-charcoal-900">{facility.sectorType}</p>
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
                <Link
                    to={`/facilities/${facility.id}`}
                    className="flex-1 btn btn-secondary text-center text-sm py-2"
                >
                    View Details
                </Link>
                <button
                    onClick={() => onEdit(facility)}
                    className="btn btn-ghost p-2 hover:bg-ember-50 hover:text-ember-600"
                    title="Edit"
                >
                    <Edit2 className="w-4 h-4" />
                </button>
                <button
                    onClick={() => onDelete(facility.id)}
                    className="btn btn-ghost p-2 hover:bg-red-50 hover:text-red-600"
                    title="Delete"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
